import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gamepad2, Wrench, Printer, ShieldCheck, PartyPopper, ArrowRight, MapPin, Clock, Zap, Trophy, Timer, BadgeCheck, FileSignature } from "lucide-react";
import { CheckoutButton } from "@/components/CheckoutButton";
import { useWaiver } from "@/components/Layout";
import { useContent, parseList, parseLines } from "@/lib/content";
import { FloatingSprites, SpriteParade, PixelSprite } from "@/components/PixelSprites";
import { ServicePicker } from "@/components/ServicePicker";

const PILLARS = [
  { icon: Gamepad2, to: "/lounge", testId: "pillar-gaming-card", span: "md:col-span-8 md:row-span-2", imgKey: "images.arcade", accent: "text-neon-cyan" },
  { icon: Wrench, to: "/repair", testId: "pillar-repair-card", span: "md:col-span-4", imgKey: "images.repair", accent: "text-neon-gold" },
  { icon: Printer, to: "/maker", testId: "pillar-maker-card", span: "md:col-span-4", imgKey: "images.maker", accent: "text-neon-magenta" },
  { icon: ShieldCheck, to: "/business-it", testId: "pillar-it-card", span: "md:col-span-6", imgKey: "images.it", accent: "text-biz-cyan" },
  { icon: PartyPopper, to: "/events", testId: "pillar-events-card", span: "md:col-span-6", imgKey: "images.events", accent: "text-neon-pink" },
];

const STAT_ICONS = [Gamepad2, Timer, Trophy, BadgeCheck];

export default function Home() {
  const openWaiver = useWaiver();
  const content = useContent();
  const hours = parseList(content["settings.hours"]);
  const marquee = parseLines(content["home.marquee"]);
  const hh = (content["home.hero_h1"] || "").split("|");
  const pillarsH2 = (content["home.pillars_h2"] || "").split("|");
  const pillarRows = parseList(content["home.pillars"]);
  const pillars = PILLARS.map((p, i) => ({
    ...p, img: content[p.imgKey],
    name: pillarRows[i]?.[0] || "", desc: pillarRows[i]?.[1] || "",
    tags: (pillarRows[i]?.[2] || "").split(";").filter(Boolean), cta: pillarRows[i]?.[3] || "Learn More",
  }));
  const stats = parseList(content["home.stats"]).map((r, i) => ({ icon: STAT_ICONS[i] || Gamepad2, value: r[0], label: r[1] }));
  const glabH2 = (content["gamelab.h2"] || "").split("|");
  const glabSteps = parseList(content["gamelab.steps"]);
  const ctaH2 = (content["cta.h2"] || "").split("|");
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="crt noise relative flex min-h-[92vh] items-center overflow-hidden">
        <img src={content["images.hero"]} alt="The Game Room main floor" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="absolute inset-0 grid-bg" />
        <FloatingSprites />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-24 sm:px-6">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="mb-4 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan" data-testid="hero-eyebrow">
            {content["home.eyebrow"]}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl"
            data-testid="hero-heading">
            {hh[0]} <span className="text-neon-cyan glow-cyan anim-flicker">{hh[1]}</span> {hh[2]}
            <span className="text-neon-magenta glow-magenta"> {hh[3]}</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg" data-testid="hero-subheading">
            {content["home.hero_sub"]}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/lounge" data-testid="hero-book-gaming-button"
              className="inline-flex items-center gap-2 rounded-md bg-neon-cyan px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white">
              Book Gaming Time <ArrowRight className="h-4 w-4" />
            </Link>
            <CheckoutButton lookupKey="day_pass" testId="hero-day-pass-button"
              className="rounded-md border-2 border-neon-magenta px-7 py-3 font-display text-sm font-extrabold uppercase tracking-wide text-neon-magenta transition-colors hover:bg-neon-magenta hover:text-white">
              Day Pass · $25
            </CheckoutButton>
            <button onClick={openWaiver} data-testid="hero-sign-waiver-button"
              className="inline-flex items-center gap-2 px-2 py-3 font-display text-sm font-bold uppercase tracking-wide text-neon-gold transition-colors hover:text-white">
              <FileSignature className="h-4 w-4" /> Sign Waiver First
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
            className="mt-6">
            <ServicePicker />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-xs text-slate-400" data-testid="hero-quick-info">
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-neon-magenta" /> {content["home.quick_address"]}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-neon-magenta" /> {content["home.quick_hours"]}</span>
            <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-neon-gold" /> {content["home.quick_note"]}</span>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-white/10 bg-inkalt py-3" data-testid="marquee-strip">
        <div className="flex w-max animate-marquee gap-8">
          {[...marquee, ...marquee, ...marquee, ...marquee].map((m, i) => (
            <span key={i} className="flex items-center gap-8 whitespace-nowrap font-display text-sm font-extrabold uppercase tracking-widest text-slate-500">
              {m} <span className="text-neon-magenta">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* FIVE PILLARS BENTO */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="pillars-section">
        <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-cyan">{content["home.pillars_eyebrow"]}</p>
        <h2 className="mb-12 max-w-2xl font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
          {pillarsH2[0]} <span className="text-neon-magenta">{pillarsH2[1]}</span> {pillarsH2[2]}
        </h2>
        <div className="grid grid-cols-12 gap-5">
          {pillars.map((p, idx) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: idx * 0.08 }}
              className={`group relative col-span-12 overflow-hidden rounded-xl border border-white/10 ${p.span}`}>
              <div data-testid={p.testId} className="card-hover crt relative flex h-full min-h-[260px] flex-col justify-end overflow-hidden">
                <img src={p.img} alt={p.name} loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover opacity-40 transition-opacity duration-500 group-hover:opacity-55" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-transparent" />
                <div className="relative z-10 p-6 sm:p-8">
                  <p.icon className={`mb-3 h-7 w-7 ${p.accent}`} />
                  <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-white sm:text-2xl">{p.name}</h3>
                  <p className="mt-2 max-w-md text-sm text-slate-300">{p.desc}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-slate-300">{t}</span>
                    ))}
                  </div>
                  <Link to={p.to} data-testid={`${p.testId}-cta`}
                    className={`mt-5 inline-flex items-center gap-2 font-display text-xs font-extrabold uppercase tracking-wide ${p.accent} transition-colors hover:text-white`}>
                    {p.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AI GAME LAB */}
      <section className="relative overflow-hidden border-y border-white/10 bg-inkalt/50" data-testid="game-lab-section">
        <div className="absolute inset-0 grid-bg" />
        <PixelSprite name="ghost" size={64} className="anim-drift absolute right-[6%] top-[12%] opacity-70" />
        <PixelSprite name="star" size={44} className="anim-bob-slow absolute bottom-[14%] left-[45%] opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-magenta">{content["gamelab.eyebrow"]}</p>
              <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white sm:text-5xl" data-testid="game-lab-heading">
                {glabH2[0]} <span className="text-neon-magenta glow-magenta">{glabH2[1]}</span>
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300 sm:text-lg">
                {content["gamelab.desc"]}
              </p>
              <div className="mt-8 space-y-4">
                {glabSteps.map((s, i) => (
                  <div key={i} className="flex gap-4" data-testid={`gamelab-step-${i}`}>
                    <span className="font-display text-xl font-black text-neon-cyan glow-cyan">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <p className="font-display text-sm font-extrabold uppercase tracking-wide text-white">{s[0]}</p>
                      <p className="text-sm text-slate-400">{s[1]}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/ai-studio" data-testid="game-lab-try-free-button"
                  className="inline-flex items-center gap-2 rounded-md bg-neon-magenta px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:bg-neon-pink">
                  Try It Free <ArrowRight className="h-4 w-4" />
                </Link>
                <span className="font-mono text-xs text-slate-500">{content["gamelab.note"]}</span>
              </div>
            </div>
            <div className="glass relative rounded-xl p-6 sm:p-8">
              <PixelSprite name="coin" size={40} className="anim-bob absolute -top-4 right-8" />
              <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Live example</p>
              <div className="rounded-lg border border-white/10 bg-ink p-4">
                <p className="font-mono text-xs text-neon-cyan">{content["gamelab.example_prompt"]}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="h-2 w-2 animate-pulse-glow rounded-full bg-neon-cyan" />
                  <span className="font-mono text-[11px] text-slate-500">AI building… ~78 seconds</span>
                </div>
              </div>
              <div className="mt-4 rounded-lg border border-neon-cyan/30 bg-neon-cyan/5 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display text-sm font-extrabold uppercase text-white">{content["gamelab.example_title"]}</p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{content["gamelab.example_meta"]}</p>
                  </div>
                  <Gamepad2 className="h-6 w-6 text-neon-cyan" />
                </div>
                <div className="mt-3 flex gap-2">
                  <span className="flex-1 rounded-md bg-neon-cyan px-3 py-2 text-center font-display text-[11px] font-extrabold uppercase text-ink">Play</span>
                  <span className="rounded-md border border-white/15 px-3 py-2 font-display text-[11px] font-extrabold uppercase text-slate-300">Share</span>
                  <span className="rounded-md border border-neon-gold/40 px-3 py-2 font-display text-[11px] font-extrabold uppercase text-neon-gold">Tweak</span>
                  <span className="rounded-md border border-neon-green/40 px-3 py-2 font-display text-[11px] font-extrabold uppercase text-neon-green">Export</span>
                </div>
              </div>
              <p className="mt-4 text-center font-mono text-[11px] text-slate-500">{content["gamelab.example_caption"]}</p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/10 bg-inkalt/50" data-testid="stats-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-14 sm:px-6 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <s.icon className="mx-auto mb-2 h-6 w-6 text-neon-cyan" />
              <p className="font-display text-3xl font-black text-white">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOURS / LOCATION / WAIVER */}
      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-24 sm:px-6 lg:grid-cols-3" data-testid="visit-section">
        <div className="glass rounded-xl p-8">
          <Clock className="mb-4 h-7 w-7 text-neon-cyan" />
          <h3 className="mb-4 font-display text-xl font-extrabold uppercase text-white">{content["visit.hours_h"]}</h3>
          <div className="space-y-2">
            {hours.map((h) => (
              <p key={h[0]} className="flex justify-between font-mono text-sm text-slate-400">
                <span>{h[0]}</span><span className="text-slate-200">{h[1]}</span>
              </p>
            ))}
          </div>
        </div>
        <div className="glass rounded-xl p-8">
          <MapPin className="mb-4 h-7 w-7 text-neon-magenta" />
          <h3 className="mb-4 font-display text-xl font-extrabold uppercase text-white">{content["visit.findus_h"]}</h3>
          <p className="text-sm leading-relaxed text-slate-300">{content["settings.address1"]}<br />{content["settings.address2"]}</p>
          <p className="mt-3 text-sm text-slate-400">{content["visit.findus_text"]}</p>
          <p className="mt-3 font-mono text-sm text-neon-cyan">{content["settings.phone"]}</p>
        </div>
        <div className="glass relative overflow-hidden rounded-xl p-8">
          <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-neon-gold/15 blur-3xl" />
          <FileSignature className="mb-4 h-7 w-7 text-neon-gold" />
          <h3 className="mb-4 font-display text-xl font-extrabold uppercase text-white">{content["visit.waiver_h"]}</h3>
          <p className="mb-6 text-sm text-slate-300">{content["visit.waiver_text"]}</p>
          <button onClick={openWaiver} data-testid="visit-sign-waiver-button"
            className="w-full rounded-md bg-neon-gold px-5 py-3 font-display text-xs font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white">
            Sign Digital Waiver
          </button>
        </div>
      </section>

      {/* CTA BANNER */}
      <SpriteParade />
      <section className="relative overflow-hidden border-t border-white/10" data-testid="cta-banner">
        <div className="absolute inset-0 grid-bg" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 text-center sm:px-6">
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-black uppercase tracking-tight text-white sm:text-5xl">
            {ctaH2[0]} <span className="text-neon-cyan glow-cyan">{ctaH2[1]}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-400">{content["cta.text"]}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CheckoutButton lookupKey="day_pass" testId="cta-day-pass-button"
              className="rounded-md bg-neon-cyan px-8 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-ink transition-colors hover:bg-white">
              Get Day Pass · $25
            </CheckoutButton>
            <Link to="/events" data-testid="cta-events-button"
              className="rounded-md border border-white/20 px-8 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-white transition-colors hover:border-neon-magenta hover:text-neon-magenta">
              Plan An Event
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
